import {
  Box,
  Flex,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import {
  FiChevronLeft,
  FiChevronRight,
  FiDatabase,
  FiRefreshCw,
} from "react-icons/fi"

import { OrganisationsService } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import useCustomToast from "@/hooks/useCustomToast"

export const Route = createFileRoute("/_layout/data-browser")({
  component: DataBrowser,
})

const TABLES = [
  "tag",
  "tagreading",
  "tagevent",
  "reader",
  "organisation",
  "user",
  "command",
  "mqttsession",
  "tagtemplate",
  "resourcepermission",
]

const PAGE_SIZE = 50

function DataBrowser() {
  const showToast = useCustomToast()
  const [selectedTable, setSelectedTable] = useState<string>("tag")
  const [data, setData] = useState<{
    columns: string[]
    rows: any[]
    total: number
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [offset, setOffset] = useState(0)
  const [orgs, setOrgs] = useState<any[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>("")

  useEffect(() => {
    OrganisationsService.readOrganisations({ limit: 100 })
      .then((res: any) => setOrgs(Array.isArray(res) ? res : (res?.data ?? [])))
      .catch(() => {})
    fetchTable("tag", 0, "")
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTable = async (table: string, off: number, orgId?: string) => {
    setLoading(true)
    const token = localStorage.getItem("access_token")
    const base = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
    const resolvedOrgId = orgId !== undefined ? orgId : selectedOrgId
    const orgParam = resolvedOrgId
      ? `&organisation_id=${encodeURIComponent(String(resolvedOrgId))}`
      : ""
    const url = `${base}/api/v1/admin/data-browser/tables/${table}?limit=${PAGE_SIZE}&offset=${off}${orgParam}`
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      const d = await res.json()
      setData(d)
    } catch (e: any) {
      showToast.showErrorToast(`Failed to load ${table}: ${e.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectTable = (table: string) => {
    setSelectedTable(table)
    setOffset(0)
    fetchTable(table, 0, selectedOrgId)
  }

  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId)
    setOffset(0)
    fetchTable(selectedTable, 0, orgId)
  }

  const handlePage = (newOffset: number) => {
    setOffset(newOffset)
    fetchTable(selectedTable, newOffset)
  }

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1

  return (
    <Box>
      <Flex
        align="flex-start"
        justify="space-between"
        mb={{ base: 5, md: 8 }}
        wrap="wrap"
        gap={4}
      >
        <Box minW={0}>
          <Heading size={{ base: "lg", md: "xl" }} color="white" mb={2}>
            Data Browser
          </Heading>
          <Text color="gray.400" fontSize={{ base: "sm", md: "md" }}>
            Browse TimescaleDB tables — superuser only
          </Text>
        </Box>
        {orgs.length > 0 && (
          <Box w={{ base: "100%", sm: "auto" }}>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Filter by organization
            </Text>
            <select
              value={selectedOrgId}
              onChange={(e) => handleOrgChange(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "8px",
                color: "white",
                padding: "8px 14px",
                fontSize: "14px",
                width: "100%",
                minWidth: "220px",
              }}
            >
              <option value="" style={{ background: "#1a1f37" }}>
                All organizations
              </option>
              {orgs.map((o: any) => (
                <option
                  key={o.id}
                  value={o.id}
                  style={{ background: "#1a1f37" }}
                >
                  {o.name}
                </option>
              ))}
            </select>
          </Box>
        )}
      </Flex>

      <Flex
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 6 }}
        align="flex-start"
      >
        {/* Table list */}
        <Box
          minW={{ base: "100%", md: "180px" }}
          w={{ base: "100%", md: "auto" }}
        >
          <VisionCard>
            <Text
              fontSize="xs"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={3}
            >
              Tables
            </Text>
            <VStack gap={1} align="stretch">
              {TABLES.map((t) => (
                <Box
                  key={t}
                  px={3}
                  py={2}
                  borderRadius="8px"
                  cursor="pointer"
                  bg={
                    selectedTable === t
                      ? "rgba(118, 99, 243, 0.2)"
                      : "transparent"
                  }
                  border={
                    selectedTable === t
                      ? "1px solid rgba(118, 99, 243, 0.4)"
                      : "1px solid transparent"
                  }
                  onClick={() => handleSelectTable(t)}
                  _hover={{ bg: "rgba(255,255,255,0.05)" }}
                >
                  <Text
                    color={selectedTable === t ? "white" : "gray.400"}
                    fontSize="sm"
                    fontFamily="mono"
                  >
                    {t}
                  </Text>
                </Box>
              ))}
            </VStack>
          </VisionCard>
        </Box>

        {/* Data grid */}
        <Box flex={1} minW={0} w="100%">
          <VisionCard p={{ base: 3, md: "22px" }}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "stretch", md: "center" }}
              gap={3}
              mb={4}
            >
              <HStack gap={3} flexWrap="wrap">
                <Text color="white" fontWeight="bold" fontFamily="mono">
                  {selectedTable}
                </Text>
                {data && (
                  <Badge colorScheme="purple" size="sm">
                    {data.total.toLocaleString()} rows
                  </Badge>
                )}
              </HStack>
              <HStack gap={2}>
                {data && totalPages > 1 && (
                  <HStack gap={1}>
                    <VisionButton
                      variant="dark"
                      size="sm"
                      onClick={() =>
                        handlePage(Math.max(0, offset - PAGE_SIZE))
                      }
                      disabled={offset === 0}
                    >
                      <FiChevronLeft />
                    </VisionButton>
                    <Text color="gray.400" fontSize="xs" px={2}>
                      {currentPage} / {totalPages}
                    </Text>
                    <VisionButton
                      variant="dark"
                      size="sm"
                      onClick={() => handlePage(offset + PAGE_SIZE)}
                      disabled={offset + PAGE_SIZE >= (data?.total ?? 0)}
                    >
                      <FiChevronRight />
                    </VisionButton>
                  </HStack>
                )}
                <VisionButton
                  variant="dark"
                  size="sm"
                  onClick={() => fetchTable(selectedTable, offset)}
                  loading={loading}
                >
                  <FiRefreshCw />
                </VisionButton>
              </HStack>
            </Flex>

            {loading && !data && (
              <Flex justify="center" py={12}>
                <Spinner color="white" />
              </Flex>
            )}

            {!loading && !data && (
              <Flex direction="column" align="center" py={12}>
                <IconBox variant="glass" size="lg" mb={4}>
                  <FiDatabase fontSize="24px" color="white" />
                </IconBox>
                <Text color="gray.400">No data found</Text>
              </Flex>
            )}

            {data && (
              <Box overflowX="auto">
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr>
                      {data.columns.map((col) => (
                        <th
                          key={col}
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            color: "rgba(255,255,255,0.5)",
                            borderBottom: "1px solid rgba(255,255,255,0.1)",
                            fontFamily: "monospace",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => (
                      <tr
                        key={i}
                        style={{
                          background:
                            i % 2 === 0
                              ? "transparent"
                              : "rgba(255,255,255,0.02)",
                        }}
                      >
                        {data.columns.map((col) => (
                          <td
                            key={col}
                            style={{
                              padding: "7px 12px",
                              color:
                                row[col] === null
                                  ? "rgba(255,255,255,0.2)"
                                  : "rgba(255,255,255,0.85)",
                              borderBottom: "1px solid rgba(255,255,255,0.04)",
                              fontFamily: "monospace",
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row[col] ?? "NULL"}
                          >
                            {row[col] === null ? (
                              <span
                                style={{
                                  color: "rgba(255,255,255,0.2)",
                                  fontStyle: "italic",
                                }}
                              >
                                NULL
                              </span>
                            ) : (
                              row[col]
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {data.rows.length === 0 && (
                  <Text
                    color="gray.500"
                    textAlign="center"
                    py={8}
                    fontSize="sm"
                  >
                    Table is empty
                  </Text>
                )}
              </Box>
            )}
          </VisionCard>
        </Box>
      </Flex>
    </Box>
  )
}
